import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Shared SpacePulse logo — rocket emblem + two-weight wordmark.
 * Used in HomePage hero and Layout sidebar.
 *
 * Props:
 *   size      — number (px) controlling the icon size
 *   showText  — bool (default true)
 *   color     — stroke / fill color (default '#fff')
 */
export default function SpacePulseLogo({ size = 40, showText = true, color = '#fff' }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, userSelect: 'none' }}>

      {/* ── Rocket emblem SVG ── */}
      <Box
        component="svg"
        viewBox="0 0 56 56"
        sx={{ width: size, height: size, flexShrink: 0 }}
      >
        {/* Outer orbit ring */}
        <circle cx="28" cy="28" r="25"
          fill="none" stroke={color} strokeWidth="1.2" strokeOpacity="0.3" />

        {/* Diagonal orbit arc (tilted ring like a planet) */}
        <ellipse cx="28" cy="28" rx="25" ry="9"
          fill="none" stroke={color} strokeWidth="0.8" strokeOpacity="0.18"
          transform="rotate(-30 28 28)" />

        {/* Rocket body — sleek elongated shape */}
        <path
          d="M28 10
             C31.5 14, 34 20, 34 29
             L28 33
             L22 29
             C22 20, 24.5 14, 28 10 Z"
          fill={color} fillOpacity="0.95"
        />

        {/* Left fin */}
        <path d="M22 28 L16 36 L22 33 Z"
          fill={color} fillOpacity="0.65" />

        {/* Right fin */}
        <path d="M34 28 L40 36 L34 33 Z"
          fill={color} fillOpacity="0.65" />

        {/* Porthole */}
        <circle cx="28" cy="24" r="3.2"
          fill="none" stroke={color} strokeWidth="1.2" strokeOpacity="0.7" />
        <circle cx="28" cy="24" r="1.2"
          fill={color} fillOpacity="0.9" />

        {/* Exhaust flame — 3 tapered jets */}
        <path d="M25 33 Q26 39 28 41 Q30 39 31 33"
          fill={color} fillOpacity="0.22" />
        <path d="M26.5 33 Q27.2 37 28 38.5 Q28.8 37 29.5 33"
          fill={color} fillOpacity="0.45" />

        {/* Small dot at top of rocket */}
        <circle cx="28" cy="10" r="1"
          fill={color} fillOpacity="0.6" />
      </Box>

      {/* ── Wordmark — horizontal, same baseline ── */}
      {showText && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0 }}>
          <Typography sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 900,
            fontSize: size * 0.44,
            color,
            letterSpacing: size * 0.04,
            textTransform: 'uppercase',
            lineHeight: 1,
          }}>
            Space
          </Typography>
          <Typography sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 300,
            fontSize: size * 0.44,
            color,
            letterSpacing: size * 0.04,
            textTransform: 'uppercase',
            lineHeight: 1,
            opacity: 0.65,
          }}>
            Pulse
          </Typography>
        </Box>
      )}
    </Box>
  );
}
